// TelcoTrack — návrh smlouvy (fill_data): doplnění z podkladů, vklad na katastr, rozpor šířky/GP.

import type { BremenaInstrumentFillData } from './bremena-instrument-fill.types';
import { normalizeBremenaInstrumentFillData } from './bremena-instrument-fill.types';
import { applyBremenaStavbaSpecDefaults, type BremenaStavbaSpecFields } from './bremena-stavba-fill.util';
import { bremenaTemplateFamilyForInstrument } from './templates/template-catalog';
import { resolveBremenaTemplateVariant } from './templates/template-selection';
import type { BremenaObjectKind } from './templates/template-catalog.types';

/** Veřejný vstup NV ČÚZK — návrh vkládá uživatel, TelcoTrack ho neodesílá. */
export const BREMENA_NV_CUZK_URL = 'https://nv.cuzk.gov.cz/Web/Uvod.aspx';

const NEZAPSANE_KN_VARIANTS = new Set(['sk_3_4_nezapsane_kn', 'sosb_4_4_nezapsane_kn']);

const OBJECT_KINDS = new Set<BremenaObjectKind>(['pozemek', 'budova', 'pozemek_budova', 'jednotka']);

export function isBremenaFillValueEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === '';
}

export function bremenaFillManualKeys(
  draft: BremenaInstrumentFillData | Record<string, unknown> | null | undefined,
): string[] {
  const normalized = normalizeBremenaInstrumentFillData(draft);
  return normalized?.manualKeys ? [...normalized.manualKeys] : [];
}

/**
 * Doplní do návrhu jen prázdná pole. Klíče v `manualKeys` se nepřepíší
 * (ani když je uživatel vymazal). Podklady sem patří jen jako `sources` —
 * pravda vlastníků, parcel a specifikace zůstává mimo `fill_data`.
 */
export function applyBremenaFillSources(
  draft: BremenaInstrumentFillData | Record<string, unknown> | null | undefined,
  sources: Partial<BremenaInstrumentFillData> | Record<string, unknown> | null | undefined,
): BremenaInstrumentFillData | null {
  const base = {
    ...(normalizeBremenaInstrumentFillData(draft) ?? {}),
  } as Record<string, unknown>;
  const manual = new Set(bremenaFillManualKeys(base));
  const src = sources && typeof sources === 'object' ? sources : {};
  for (const [key, value] of Object.entries(src)) {
    if (key === 'manualKeys') continue;
    if (manual.has(key)) continue;
    if (!isBremenaFillValueEmpty(base[key])) continue;
    if (isBremenaFillValueEmpty(value)) continue;
    base[key] = value;
  }
  if (manual.size) base['manualKeys'] = [...manual];
  return normalizeBremenaInstrumentFillData(base);
}

/** Hodnoty ze specifikace stavby a parcel. Nečte návrh (`fill_data`). */
export function bremenaFillSourcesFromPodklady(input: {
  stavba?: BremenaStavbaSpecFields | null;
  instrument?: { sapCode?: string | null; kiaCode?: string | null } | null;
  parcels?: ReadonlyArray<{
    obec?: string | null;
    okres?: string | null;
    Obec?: string | null;
    Okres?: string | null;
  }> | null;
}): Partial<BremenaInstrumentFillData> {
  const resolved = applyBremenaStavbaSpecDefaults(null, input.instrument ?? null, input.stavba ?? null);
  const parcelObec = firstParcelText(input.parcels, 'obec', 'Obec');
  const parcelOkres = firstParcelText(input.parcels, 'okres', 'Okres');
  return {
    ...resolved,
    ...(resolved.obec ? {} : parcelObec ? { obec: parcelObec } : {}),
    ...(resolved.okres ? {} : parcelOkres ? { okres: parcelOkres } : {}),
  };
}

function firstParcelText(
  parcels: ReadonlyArray<Record<string, unknown>> | null | undefined,
  ...keys: string[]
): string | undefined {
  for (const parcel of parcels ?? []) {
    for (const key of keys) {
      const value = parcel[key];
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
    }
  }
  return undefined;
}

export interface BremenaKatastrAssistInput {
  instrumentType?: string | null;
  contractStatus?: string | null;
  nezapsanoVKn?: boolean | null;
  templateVariant?: string | null;
  /** `false` = věc není zapsaná v KN. */
  zapsanoVKn?: boolean | null;
}

export interface BremenaKatastrAssistEligibility {
  eligible: boolean;
  reasonCs: string | null;
}

function isNezapsanoVKn(input: BremenaKatastrAssistInput): boolean {
  const variant = (input.templateVariant ?? '').trim();
  return input.nezapsanoVKn === true || input.zapsanoVKn === false || NEZAPSANE_KN_VARIANTS.has(variant);
}

/**
 * Vklad na NV ČÚZK jen pro podepsanou SK, a pro zřízení když je věc zapsaná v KN.
 * SoSB, SUZ a nezapsáno (3.4 / 4.4) vklad nemají.
 */
export function bremenaKatastrAssistEligibility(
  input: BremenaKatastrAssistInput,
): BremenaKatastrAssistEligibility {
  const type = (input.instrumentType ?? '').trim();
  const status = (input.contractStatus ?? '').trim().toLowerCase();
  const nezapsano = isNezapsanoVKn(input);

  if (type === 'sosb') return { eligible: false, reasonCs: 'SoSB se na katastr nepodává.' };
  if (type === 'suz') return { eligible: false, reasonCs: 'SUZ se na katastr nepodává.' };
  if (type === 'zakonny_104a') return { eligible: false, reasonCs: '§104a se na katastr nepodává.' };
  if (type !== 'sk_konecna' && type !== 'zrizeni_sluzebnosti') {
    return {
      eligible: false,
      reasonCs: 'Vklad je jen pro podepsanou konečnou smlouvu (SK) nebo zřízení zapsané v KN.',
    };
  }
  if (nezapsano) {
    return { eligible: false, reasonCs: 'Nezapsáno v KN (3.4 / 4.4) — vklad se nepodává.' };
  }
  if (status !== 'podepsana') {
    return {
      eligible: false,
      reasonCs: 'Na katastr jde až podepsaná smlouva, ne HTML návrh.',
    };
  }
  return { eligible: true, reasonCs: null };
}

export interface BremenaCaseDraftSnapshot {
  instrumentId: string;
  sirkaVb?: string | number | null;
  gpCislo?: string | null;
}

export interface BremenaCaseFieldConflict {
  field: 'sirkaVb' | 'gpCislo';
  messageCs: string;
  distinctValues: string[];
}

function normalizeSirka(value: unknown): string {
  const text = value == null ? '' : String(value).trim().replace(/\s+/g, '').replace(',', '.');
  if (!text) return '';
  const n = Number(text);
  return Number.isFinite(n) ? String(n) : text.toLowerCase();
}

function normalizeGp(value: unknown): string {
  if (value == null) return '';
  return String(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

function distinctFilled(
  drafts: readonly BremenaCaseDraftSnapshot[],
  read: (row: BremenaCaseDraftSnapshot) => string,
): string[] {
  const values = new Set<string>();
  for (const row of drafts) {
    const value = read(row);
    if (value) values.add(value);
  }
  return [...values];
}

/** Varování, když se šířka VB nebo číslo GP liší mezi návrhy stejného případu. */
export function bremenaCaseDraftFieldConflicts(
  drafts: readonly BremenaCaseDraftSnapshot[],
): BremenaCaseFieldConflict[] {
  const conflicts: BremenaCaseFieldConflict[] = [];
  const sirky = distinctFilled(drafts, (row) => normalizeSirka(row.sirkaVb));
  if (sirky.length > 1) {
    conflicts.push({
      field: 'sirkaVb',
      distinctValues: sirky,
      messageCs: `Šířka VB se v návrzích případu liší (${sirky.join(' / ')} m).`,
    });
  }
  const gps = distinctFilled(drafts, (row) => normalizeGp(row.gpCislo));
  if (gps.length > 1) {
    conflicts.push({
      field: 'gpCislo',
      distinctValues: gps,
      messageCs: `Číslo geometrického plánu se v návrzích případu liší (${gps.join(' / ')}).`,
    });
  }
  return conflicts;
}

export interface BremenaContractEditorSections {
  predmet: boolean;
  sirkaVb: boolean;
  budova: boolean;
  jednotky: boolean;
  nahrada: boolean;
  platba: boolean;
  gp: boolean;
  obec: boolean;
  zastupce: boolean;
  suz: boolean;
  bezSankci: boolean;
}

function asObjectKind(value: string | null | undefined): BremenaObjectKind | undefined {
  const key = (value ?? '').trim() as BremenaObjectKind;
  return OBJECT_KINDS.has(key) ? key : undefined;
}

/** Které bloky editoru patří k vybrané variantě (ne jeden univerzální modal). */
export function bremenaContractEditorSections(input: {
  instrumentType?: string | null;
  templateVariant?: string | null;
  objectKind?: string | null;
  nezapsanoVKn?: boolean | null;
  charOsKody?: readonly (string | null | undefined)[] | null;
}): BremenaContractEditorSections {
  const objectKind = asObjectKind(input.objectKind);
  const selection = resolveBremenaTemplateVariant({
    instrumentType: input.instrumentType,
    variantOverride: input.templateVariant,
    objectKindOverride: objectKind,
    nezapsanoVKn: input.nezapsanoVKn,
    charOsKody: (input.charOsKody ?? []).map((k) => (k ?? '').trim()).filter(Boolean),
    objects: [],
  });
  const family = selection.variant?.family ?? bremenaTemplateFamilyForInstrument(input.instrumentType);
  const kind = objectKind ?? selection.objectKind;
  const zapsano = input.nezapsanoVKn === true ? false : selection.variant ? selection.variant.zapsanoVKn : selection.zapsanoVKn;
  const hasBuilding = kind === 'budova' || kind === 'pozemek_budova' || kind === 'jednotka';
  const hasLand = kind !== 'budova';
  const charOs = new Set((input.charOsKody ?? []).map((k) => (k ?? '').trim()).filter(Boolean));
  const obecKraj = charOs.has('4') || charOs.has('5');
  /** Právnická osoba (20) — rada / valná hromada doložka. */
  const privateCompany = charOs.has('20');
  const contractFamily = family === 'sk' || family === 'sosb' || family === 'suz';

  return {
    predmet: true,
    sirkaVb: (family === 'sk' || family === 'sosb') && hasLand && zapsano,
    budova: hasBuilding,
    jednotky: kind === 'jednotka' || kind === 'pozemek_budova',
    nahrada: contractFamily || family == null,
    platba: contractFamily,
    gp: family === 'sk' && zapsano,
    obec: obecKraj || privateCompany,
    zastupce: true,
    suz: family === 'suz',
    bezSankci: obecKraj,
  };
}
